import React, { useMemo, useState, useEffect } from 'react';
import { 
  DndContext, 
  closestCenter,
  KeyboardSensor,
  TouchSensor,
  MouseSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  rectIntersection,
  useDroppable,
} from '@dnd-kit/core';
import { ClassComponentRegistry, sortByOrder, EnhancedCharacter } from '../../utils/ClassComponentRegistry';
import { CharacterData } from '../../types/character';
import { useSectionOrder } from '../../contexts/SectionOrderContext';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import DraggableSection from '../Common/DraggableSection';
import BasicInfo from './BasicInfo';
import Traits from './Traits';
import Experiences from './Experiences';
import Inventory from './Inventory';
import EquipmentSlots from './EquipmentSlots';
import DomainCardManager from './DomainCardManager';
import { LevelUpWizard } from '../LevelUpWizard/LevelUpWizard';
import './CharacterSheet.css';
import { ArrowBigDownDash, ArrowBigUpDash } from 'lucide-react';

interface CharacterSheetProps {
  enhancedCharacter: EnhancedCharacter;
  updateCharacterData: (updates: any) => void;
  disableAutoSave?: () => void;
  enableAutoSave?: () => void;
}

interface DropColumnProps {
  id: string;
  items: string[];
  isDragging: boolean;
  isOver: boolean;
  className?: string;
  children: React.ReactNode;
}

const DropColumn: React.FC<DropColumnProps> = ({ 
  id, 
  items, 
  isDragging, 
  isOver, 
  className = '',
  children 
}) => {
  const { setNodeRef } = useDroppable({
    id,
  });

  const dropZoneClasses = [
    'character-sheet__drop-zone',
    className,
    isOver ? 'character-sheet__drop-zone--active' : ''
  ].filter(Boolean).join(' ');

  return (
    <div className="character-sheet__column">
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div
          ref={setNodeRef}
          className={dropZoneClasses}
        >
          {children}
          {/* Show empty drop zone when dragging and column is empty */}
          {items.length === 0 && isDragging && (
            <div className="character-sheet__empty-drop-zone">
              <p className="character-sheet__empty-text">Drop sections here</p>
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
};

const CharacterSheet: React.FC<CharacterSheetProps> = ({ enhancedCharacter, updateCharacterData, disableAutoSave, enableAutoSave }) => {
  const { sectionOrder, setSectionOrder, columnAssignment, setColumnAssignment, setAllSectionsCollapsed, isLoading } = useSectionOrder();
  const isDesktop = useIsDesktop();
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverColumn, setDragOverColumn] = useState<'left' | 'right' | null>(null);
  const [expandPulse, setExpandPulse] = useState(false);
  const [collapsePulse, setCollapsePulse] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  // Set up sensors for drag and drop with mobile optimization
  const sensors = useSensors(
    // Mouse sensor for desktop - smaller activation distance
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before starting drag on desktop
      },
    }),
    // Touch sensor for mobile - larger activation distance and delay to prevent scroll conflicts
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250, // 250ms delay to distinguish from scrolling
        tolerance: 5, // Allow 5px of movement during delay
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const character = enhancedCharacter.character;

  // Get class-specific sections dynamically
  const classSpecificSections = character?.class 
    ? sortByOrder(ClassComponentRegistry.getCharacterSheetSections(character.class) || [])
    : [];

  // Helper function to check if we have domain cards to display
  const hasDomainCards = ((character?.inventory?.domainCardLoadout?.length ?? 0) + (character?.inventory?.domainCardVault?.length ?? 0)) > 0;

  // Handlers for collapse/expand all functionality
  const handleCollapseAll = () => {
    setCollapsePulse(true);
    setAllSectionsCollapsed(true);
    // Reset pulse animation
    setTimeout(() => setCollapsePulse(false), 600);
  };

  const handleExpandAll = () => {
    setExpandPulse(true);
    setAllSectionsCollapsed(false);
    // Reset pulse animation
    setTimeout(() => setExpandPulse(false), 600);
  };

  // Define all possible sections with their metadata
  const sectionDefinitions = useMemo(() => {
    const sections = new Map();
    
    // Core sections
    sections.set('character-traits', {
      id: 'character-traits',
      title: 'Character Traits',
      component: (
        <DraggableSection id="character-traits" title="Character Traits" defaultExpanded={false} priority="high">
          <Traits enhancedCharacter={enhancedCharacter} updateCharacterData={updateCharacterData} />
        </DraggableSection>
      )
    });

    sections.set('basic-info', {
      id: 'basic-info',
      title: 'Basic Information',
      component: (
        <DraggableSection id="basic-info" title="Basic Information" defaultExpanded={false} priority="high">
          <BasicInfo
            enhancedCharacter={enhancedCharacter}
            updateCharacterData={updateCharacterData}
            disableAutoSave={disableAutoSave}
            enableAutoSave={enableAutoSave}
            onLevelUp={() => setWizardOpen(true)}
          />
        </DraggableSection>
      )
    });

    sections.set('equipped-items', {
      id: 'equipped-items',
      title: 'Equipped Items',
      component: (
        <DraggableSection id="equipped-items" title="Equipped Items" defaultExpanded={false} priority="high">
          <EquipmentSlots enhancedCharacter={enhancedCharacter} />
        </DraggableSection>
      )
    });

    sections.set('experiences', {
      id: 'experiences',
      title: 'Experiences',
      component: (
        <DraggableSection id="experiences" title="Experiences" defaultExpanded={false} priority="high">
          <Experiences enhancedCharacter={enhancedCharacter} updateCharacterData={updateCharacterData} />
        </DraggableSection>
      )
    });

    sections.set('inventory', {
      id: 'inventory',
      title: 'Inventory',
      component: (
        <DraggableSection id="inventory" title="Inventory" defaultExpanded={false} priority="medium">
          <Inventory enhancedCharacter={enhancedCharacter} />
        </DraggableSection>
      )
    });

    // Unified domain cards section
    if (hasDomainCards) {
      sections.set('domain-cards', {
        id: 'domain-cards',
        title: 'Domain Cards',
        component: (
          <DraggableSection id="domain-cards" title="Domain Cards" defaultExpanded={false} priority="medium">
            <DomainCardManager 
              enhancedCharacter={enhancedCharacter}
            />
          </DraggableSection>
        )
      });
    }

    // Add class-specific sections
    (classSpecificSections || []).forEach((section, index) => {
      const Component = section.component;
      const sectionId = `class-section-${index}`;
      
      sections.set(sectionId, {
        id: sectionId,
        title: section.title,
        component: (
          <DraggableSection 
            id={sectionId}
            title={section.title}
            defaultExpanded={section.defaultExpanded}
            priority={section.priority}
                      >
            <Component 
              enhancedCharacter={enhancedCharacter} 
            />
          </DraggableSection>
        )
      });
    });

    return sections;
  }, [enhancedCharacter, updateCharacterData, hasDomainCards, classSpecificSections]);

  // Auto-update section order when new sections are discovered
  useEffect(() => {
    if (isLoading || !character) return;
    
    const availableSectionIds = Array.from(sectionDefinitions.keys());
    const newSectionIds = availableSectionIds.filter(id => !sectionOrder.includes(id));
    
    if (newSectionIds.length > 0) {
      // Add new sections to the end of the appropriate order based on priority
      const sortedNewSections = newSectionIds.sort((a, b) => {
        
        // If both have class-section ids, sort by the numeric part
        if (a.startsWith('class-section-') && b.startsWith('class-section-')) {
          const indexA = parseInt(a.split('-')[2]);
          const indexB = parseInt(b.split('-')[2]);
          return indexA - indexB;
        }
        
        return a.localeCompare(b);
      });
      
      // Insert class sections after basic-info, other sections at the end
      const updatedOrder = [...sectionOrder];
      sortedNewSections.forEach(sectionId => {
        if (sectionId.startsWith('class-section-')) {
          // Find the position after basic-info
          const basicInfoIndex = updatedOrder.indexOf('basic-info');
          const insertIndex = basicInfoIndex >= 0 ? basicInfoIndex + 1 : 2;
          updatedOrder.splice(insertIndex, 0, sectionId);
        } else {
          // Add at the end for other sections
          updatedOrder.push(sectionId);
        }
      });
      
      setSectionOrder(updatedOrder);
    }
  }, [sectionDefinitions, sectionOrder, setSectionOrder, isLoading, character]);

  // Separate effect for updating column assignment when new sections are added
  useEffect(() => {
    if (isLoading || !character) return;
    
    const availableSectionIds = Array.from(sectionDefinitions.keys());
    const missingSectionIds = availableSectionIds.filter(id => 
      !columnAssignment.left.includes(id) && !columnAssignment.right.includes(id)
    );
    
    if (missingSectionIds.length > 0) {
      const updatedColumnAssignment = { ...columnAssignment };
      missingSectionIds.forEach((sectionId, index) => {
        if (sectionId.startsWith('class-section-')) {
          // Distribute class sections between columns to maintain balance
          const targetColumn = index % 2 === 0 ? 'left' : 'right';
          updatedColumnAssignment[targetColumn].push(sectionId);
        } else {
          // Add other sections to the shorter column for balance
          const leftCount = updatedColumnAssignment.left.length;
          const rightCount = updatedColumnAssignment.right.length;
          const targetColumn = leftCount <= rightCount ? 'left' : 'right';
          updatedColumnAssignment[targetColumn].push(sectionId);
        }
      });
      
      setColumnAssignment(updatedColumnAssignment);
    }
  }, [sectionDefinitions, columnAssignment, setColumnAssignment, isLoading, character]);

  // Floating Controls Component
  const FloatingControls = () => (
    <div className="character-sheet__floating-controls">
      <button
        className={`character-sheet__floating-btn character-sheet__floating-btn--collapse ${collapsePulse ? 'character-sheet__floating-btn--pulse' : ''}`}
        onClick={handleCollapseAll}
        data-tooltip="Collapse All Sections"
        type="button"
        aria-label="Collapse all sections"
      >
        <ArrowBigUpDash size={30} />
      </button>
      <button
        className={`character-sheet__floating-btn character-sheet__floating-btn--expand ${expandPulse ? 'character-sheet__floating-btn--pulse' : ''}`}
        onClick={handleExpandAll}
        data-tooltip="Expand All Sections"
        type="button"
        aria-label="Expand all sections"
      >
        <ArrowBigDownDash size={30} />
      </button>
    </div>
  );

  // Build ordered sections array based on user preferences
  const orderedSections = useMemo(() => {
    if (isLoading) return [];
    
    return sectionOrder
      .map(id => sectionDefinitions.get(id))
      .filter(section => section !== undefined)
      .map((section, index) => React.cloneElement(section.component, { key: section.id || index }));
  }, [sectionOrder, sectionDefinitions, isLoading]);

  // Get the IDs for sortable context
  const sectionIds = useMemo(() => {
    return sectionOrder.filter(id => sectionDefinitions.has(id));
  }, [sectionOrder, sectionDefinitions]);

  const handleMobileDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = sectionIds.indexOf(active.id as string);
      const newIndex = sectionIds.indexOf(over.id as string);
      
      const newOrder = arrayMove(sectionIds, oldIndex, newIndex);
      setSectionOrder(newOrder);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setIsDragging(true);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    
    if (!over) {
      setDragOverColumn(null);
      return;
    }

    const overId = over.id as string;
    
    // Check if the over target is in left or right column
    if (columnAssignment.left.includes(overId) || overId === 'left-column') {
      setDragOverColumn('left');
    } else if (columnAssignment.right.includes(overId) || overId === 'right-column') {
      setDragOverColumn('right');
    } else {
      setDragOverColumn(null);
    }
  };

  const handleDesktopDragEnd = (event: DragEndEvent, sourceColumn: 'left' | 'right') => {
    const { active, over } = event;
    
    // Reset drag state
    setIsDragging(false);
    setDragOverColumn(null);
    
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;
    
    // Handle dropping on empty column zones
    if (overId === 'left-column' || overId === 'right-column') {
      const targetColumn = overId === 'left-column' ? 'left' : 'right';
      
      if (sourceColumn !== targetColumn) {
        const sourceItems = columnAssignment[sourceColumn].filter(id => id !== activeId);
        const targetItems = [...columnAssignment[targetColumn], activeId];
        
        setColumnAssignment({
          ...columnAssignment,
          [sourceColumn]: sourceItems,
          [targetColumn]: targetItems
        });
      }
      return;
    }
    
    // Determine which column the drop target is in
    const overColumn = columnAssignment.left.includes(overId) ? 'left' : 'right';
    
    // If dragging within the same column
    if (sourceColumn === overColumn) {
      const columnItems = columnAssignment[sourceColumn];
      const oldIndex = columnItems.indexOf(activeId);
      const newIndex = columnItems.indexOf(overId);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newColumnItems = arrayMove(columnItems, oldIndex, newIndex);
        setColumnAssignment({
          ...columnAssignment,
          [sourceColumn]: newColumnItems
        });
      }
    } else {
      // Cross-column dragging
      const sourceItems = columnAssignment[sourceColumn].filter(id => id !== activeId);
      const targetItems = columnAssignment[overColumn];
      const targetIndex = targetItems.indexOf(overId);
      
      // Insert at the correct position in target column
      const newTargetItems = [...targetItems];
      newTargetItems.splice(targetIndex + 1, 0, activeId);
      
      setColumnAssignment({
        ...columnAssignment,
        [sourceColumn]: sourceItems,
        [overColumn]: newTargetItems
      });
    }
  };

  // Early return checks after all hooks
  if (!character) {
    return (
      <div className="character-sheet">
        <div className="character-sheet__loading">
          Loading character...
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="character-sheet">
        <div className="character-sheet__loading">
          Loading character...
        </div>
      </div>
    );
  }

  // Mobile Layout: Single column with all sections
  if (!isDesktop) {
    return (
      <div className="character-sheet">
        <FloatingControls />
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleMobileDragEnd}
        >
          <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
            <div className="character-sheet__container character-sheet__container--mobile">
              {orderedSections}
            </div>
          </SortableContext>
        </DndContext>

        {/* Level-Up Wizard Modal */}
        <LevelUpWizard
          character={character}
          isOpen={wizardOpen}
          onClose={() => setWizardOpen(false)}
          onComplete={(updatedCharacter: CharacterData) => {
            // Just update the character data, don't close wizard
            // The wizard will show completion step and user clicks "Close" to exit
            updateCharacterData(updatedCharacter);
          }}
        />
      </div>
    );
  }

  // Desktop Layout: Two columns with cross-column dragging
  const leftSections = columnAssignment.left
    .map(id => sectionDefinitions.get(id))
    .filter(section => section !== undefined)
    .map((section, index) => React.cloneElement(section.component, { key: section.id || `left-${index}` }));

  const rightSections = columnAssignment.right
    .map(id => sectionDefinitions.get(id))
    .filter(section => section !== undefined)
    .map((section, index) => React.cloneElement(section.component, { key: section.id || `right-${index}` }));

  const allDesktopItems = [...columnAssignment.left, ...columnAssignment.right];

  return (
    <div className="character-sheet">
      <FloatingControls />
      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={(event) => {
          const { active, over } = event;

          if (!over) {
            setIsDragging(false);
            setDragOverColumn(null);
            return;
          }

          const activeId = active.id as string;
          const overId = over.id as string;

          // Determine source column
          const activeInLeft = columnAssignment.left.includes(activeId);
          const sourceColumn = activeInLeft ? 'left' : 'right';

          handleDesktopDragEnd(event, sourceColumn);
        }}
      >
        <div className="character-sheet__container character-sheet__container--desktop">
          {/* Left Column */}
          <DropColumn
            id="left-column"
            items={columnAssignment.left}
            isDragging={isDragging}
            isOver={dragOverColumn === 'left'}
            className="character-sheet__drop-zone--left"
          >
            {leftSections}
          </DropColumn>

          {/* Right Column */}
          <DropColumn
            id="right-column"
            items={columnAssignment.right}
            isDragging={isDragging}
            isOver={dragOverColumn === 'right'}
            className="character-sheet__drop-zone--right"
          >
            {rightSections}
          </DropColumn>
        </div>
      </DndContext>

      {/* Level-Up Wizard Modal */}
      <LevelUpWizard
        character={character}
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onComplete={(updatedCharacter: CharacterData) => {
          // Just update the character data, don't close wizard
          // The wizard will show completion step and user clicks "Close" to exit
          updateCharacterData(updatedCharacter);
        }}
      />
    </div>
  );
};

export default CharacterSheet;